/**
 * Nothing else clears a Field error while the member fixes its field, so the page keeps saying the field is wrong. The server
 * stays the only judge: the next submit renders the error again while the field is still wrong. A change to any field inside a
 * `role="group"` that an error element describes clears that group's error too (the group never carries `aria-invalid`). The error id
 * (`<fieldId>-errors`) is built in Libraries/src/mvdmio.ASP.Razor/TagHelpers/FieldErrorSlot.cs.
 */

const INVALID_SELECTOR = '[aria-invalid="true"]';
const COMPOSITE_HOST_SELECTOR = '[data-controller~="searchable-select"], [data-controller~="rich-text-editor"]';
const COMPOSITE_VALUE_SELECTOR = 'select, input[type="hidden"]';
const ERRORS_ID_SUFFIX = "-errors";
const GROUP_SELECTOR = '[role="group"][aria-describedby]';

function focusElementOf(target: Element): Element | null {
   if (target.matches(INVALID_SELECTOR)) {
      return target;
   }
   const host = target.closest(COMPOSITE_HOST_SELECTOR);
   return host && target.matches(COMPOSITE_VALUE_SELECTOR) ? host.querySelector(INVALID_SELECTOR) : null;
}

function describedByIds(element: Element): string[] {
   return (element.getAttribute("aria-describedby") ?? "").split(/\s+/).filter(Boolean);
}

function clearMarking(element: Element, errorId: string): void {
   const remaining = describedByIds(element).filter((id) => id !== errorId);
   element.removeAttribute("aria-invalid");
   if (remaining.length > 0) {
      element.setAttribute("aria-describedby", remaining.join(" "));
   } else {
      element.removeAttribute("aria-describedby");
   }
}

function clearFieldError(errorId: string): void {
   document.getElementById(errorId)?.remove();
   for (const element of document.querySelectorAll("[aria-describedby]")) {
      if (describedByIds(element).includes(errorId)) {
         clearMarking(element, errorId);
      }
   }
}

function errorIdsOf(element: Element): string[] {
   return describedByIds(element).filter((candidate) => candidate.endsWith(ERRORS_ID_SUFFIX));
}

// The nearest ancestor group that a Field error describes: an inner group without one (a row labelled only by aria-label) is skipped.
function errorGroupOf(target: Element): Element | null {
   for (let group = target.closest(GROUP_SELECTOR); group; group = group.parentElement?.closest(GROUP_SELECTOR) ?? null) {
      if (errorIdsOf(group).length > 0) {
         return group;
      }
   }
   return null;
}

function onFieldChange(event: Event): void {
   if (!(event.target instanceof Element)) {
      return;
   }
   const focusElement = focusElementOf(event.target);
   if (focusElement) {
      for (const id of errorIdsOf(focusElement)) {
         clearFieldError(id);
      }
   }
   const group = errorGroupOf(event.target);
   if (group) {
      for (const id of errorIdsOf(group)) {
         clearFieldError(id);
      }
   }
}

export function installFieldErrorClears(): () => void {
   document.addEventListener("input", onFieldChange);
   document.addEventListener("change", onFieldChange);

   return () => {
      document.removeEventListener("input", onFieldChange);
      document.removeEventListener("change", onFieldChange);
   };
}
